/**
 * Shared type utilities for switchboard converters
 * Provides common type patterns, type guards, and generic helpers
 */
import type { LegacyNodeKind } from './legacyNodes'
import type { NodeType } from './reactFlowTypes'

/**
 * Type guard to check if a value is a non-null object
 */
export function isNonNullObject(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/**
 * Type guard to check if a value is an array
 */
export function isArray(value: unknown): value is unknown[] {
  return Array.isArray(value)
}

/**
 * Type guard to check if a value is a string
 */
export function isString(value: unknown): value is string {
  return typeof value === 'string'
}

/**
 * Type guard to check if a value is a number
 */
export function isNumber(value: unknown): value is number {
  return typeof value === 'number' && !isNaN(value)
}

/**
 * Type guard to check if a value is a boolean
 */
export function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean'
}

/**
 * Check if a node kind/type matches the given values
 * @param kind - Legacy node kind string
 * @param nodeType - React Flow node type
 * @param expectedKind - Expected legacy kind string
 * @param expectedNodeType - Expected React Flow node type
 * @returns True if the node matches
 */
export function isNodeType(
  kind: string,
  nodeType: NodeType | 'start',
  expectedKind: string,
  expectedNodeType: NodeType,
): boolean {
  return kind === expectedKind || nodeType === expectedNodeType
}

/**
 * Type for field mapping functions
 */
export type FieldMapper<T = unknown> = (
  html: Record<string, unknown>,
  nodeData: Record<string, unknown>,
  ...args: unknown[]
) => T

/**
 * Type for field value converters
 */
export type FieldConverter<TInput = unknown, TOutput = unknown> = (
  value: TInput,
) => TOutput

/**
 * Extract target value from an object, checking multiple possible field names
 * @param obj - Object to extract target from
 * @returns The target value, or undefined if not found
 */
export function extractTarget(obj: Record<string, unknown>): unknown {
  return (
    obj.target ||
    obj.target_id ||
    obj.target_uuid ||
    obj.target_node ||
    undefined
  )
}

/**
 * Normalize children array to ensure it starts with an empty object
 * Legacy format requires first element to be empty object, but handle malformed data gracefully
 * @param children - Children array from HTML
 * @returns Normalized children array
 */
export function normalizeChildrenArray(children: unknown[]): unknown[] {
  if (children.length === 0) {
    return [{}]
  }

  const firstElement = children[0]
  // If first element is not an empty object, prepend one
  if (
    typeof firstElement !== 'object' ||
    firstElement === null ||
    Object.keys(firstElement as Record<string, unknown>).length > 0
  ) {
    return [{}, ...children]
  }

  return children
}

/**
 * Generic type for node data with index signature
 */
export interface NodeDataRecord extends Record<string, unknown> {
  [key: string]: unknown
}

/**
 * Type for HTML field records
 */
export interface HtmlFieldRecord extends Record<string, unknown> {
  [key: string]: unknown
}

/**
 * Type for conversion context
 */
export interface ConversionContext {
  kind: LegacyNodeKind
  nodeType: NodeType | 'start'
  nodeId?: string
}

/**
 * Utility type to extract keys from a record type
 */
export type KeysOf<T> = keyof T & string

/**
 * Utility type to make all properties optional recursively
 */
export type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>
    }
  : T
